import { notification } from 'antd';
import { useMemo, useState } from 'react';
import { ColumnsType } from 'antd/lib/table/interface';
import { useAuth } from '../../../domain/auth/useAuth';
import { useCustomizableTableContext } from '../../table/CustomizableTableContext';
import { ExportButton } from '../../ExportButton/ExportButton';
import { IDownloadNominationCsvFormData, DownloadNominationCsvModal } from './DownloadNominationCsvModal';
import { exportNominations } from '../../../domain/nomination';

interface INominationExportButtonProps {
    filter: {
        [key: string]: any;
    };
    [key: string]: any;
}

export const NominationExportButton = ({ filter, ...props }: INominationExportButtonProps) => {
    const [exportLoading, setExportLoading] = useState<boolean>(false);
    const [popupOpen, setPopupOpen] = useState<boolean>(false);
    const { columns } = useCustomizableTableContext();
    const auth = useAuth()!;
    const isAdminRole = useMemo(() => auth.user?.isAdminRoleType(), [auth.user]);

    const exportNominationsCSVAction = async (data: IDownloadNominationCsvFormData) => {
        try {
            setExportLoading(true);
            await exportNominations({
                ...filter,
                include: isAdminRole ? 'latest_nomination_readiness_track' : '',
                fileColumnsPreset: data.columnsPreset,
                ...(data.columnsPreset === 'default' ? { fields: getFieldsList(columns) } : {}),
            });
        } catch (error: any) {
            notification.error({
                key: 'nominations-export-csv-error',
                message: error.message || 'Cannot export nominations file!',
            });
        } finally {
            setExportLoading(false);
        }
    };

    const handleSubmit = async (data: IDownloadNominationCsvFormData) => {
        setPopupOpen(false);

        await exportNominationsCSVAction(data);
    };

    return (
        <>
            <ExportButton
                key="site-export"
                actions={[{ key: 'download-nomination-csv', title: 'Download CSV', fn: () => setPopupOpen(true) }]}
                loading={exportLoading}
                {...props}
            />
            {popupOpen && <DownloadNominationCsvModal onClose={() => setPopupOpen(false)} onSubmit={handleSubmit} />}
        </>
    );
};

function getFieldsList(tableColumns: ColumnsType<any>): string {
    const csvColumns: string[] = [];

    tableColumns.forEach(column => {
        const isColumnHidden = !!column.hidden;
        if (!isColumnHidden && typeof column.key === 'string') {
            csvColumns.push(column.key);

            // Market and load zone displayed in one column in the UI table, but we split it in the CSV
            if (column.key === 'lmp_market') {
                csvColumns.push('site_load_zone');
            }
        }
    });

    return csvColumns.join(',');
}
